import { useState } from "react";
import { Wand2, Download, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ColourWheel } from "@/components/editor/ColourWheel";
import { Section, Param } from "@/components/editor/Section";
import { DEFAULT_GRADE, PRESETS, mergeGrade, toCube, downloadBlob } from "@/lib/grade";
import { api, formatApiError } from "@/lib/api";

const PRIMARIES = [
  ["exposure", "Exposure", -2, 2], ["contrast", "Contrast", -1, 1], ["saturation", "Saturation", 0, 2],
  ["temperature", "Temperature", -1, 1], ["tint", "Tint", -1, 1], ["shadows", "Shadows", -1, 1], ["highlights", "Highlights", -1, 1],
];

export const ColourPanel = ({ grade, setGrade, projectName }) => {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [look, setLook] = useState(grade.name ? { name: grade.name, notes: grade.notes } : null);
  const [lutName, setLutName] = useState(grade.name || "");
  const [size, setSize] = useState("33");
  const set = (k) => (v) => setGrade({ ...grade, [k]: v });

  const aiGrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/grade", { prompt });
      setGrade(mergeGrade({ ...data, mix: 1 }));
      setLook({ name: data.name, notes: data.notes }); setLutName(data.name);
      toast.success(`Look created: ${data.name}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const applyPreset = (p) => {
    setGrade(mergeGrade({ ...p.params, name: p.name, notes: `${p.ref} — an original interpretation.` }));
    setLook({ name: p.name, notes: `${p.ref} — our own original interpretation. Fine-tune it with the wheels below.` });
    setLutName(p.name);
  };

  const exportLut = () => {
    const name = (lutName || projectName || "Celestial Look").trim();
    downloadBlob(new Blob([toCube(grade, name, +size)], { type: "text/plain" }), `${name.replace(/[^\w-]+/g, "_")}_${size}.cube`);
    toast.success("LUT exported (.cube)");
  };

  return (
    <div data-testid="colour-panel">
      <Section title="Describe your look">
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Warm 70s golden-hour film print with creamy highlights and dense, inky shadows"
          className="bg-[#0B0C10] text-sm min-h-[72px]" data-testid="grade-prompt-input" />
        <Button onClick={aiGrade} disabled={busy || prompt.trim().length < 2} className="w-full mt-2 bg-[#FFB800] text-[#0B0C10] hover:bg-[#ffc933]" data-testid="grade-generate-button">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}Grade with AI colourist
        </Button>
        {look && <div className="mt-3 p-3 rounded-md bg-[#0B0C10] border border-[#2A2E3D]" data-testid="grade-look-notes">
          <p className="text-sm font-semibold text-[#FFB800]">{look.name}</p><p className="text-xs text-slate-400 mt-1">{look.notes}</p></div>}
      </Section>
      <Section title="Film & TV inspired looks">
        <div className="grid grid-cols-2 gap-2">
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => applyPreset(p)} data-testid={`preset-${p.id}`}
              className="text-left p-2 rounded-md bg-[#0B0C10] border border-[#2A2E3D] hover:border-[#FFB800]/70 transition-colors">
              <div className="h-5 rounded-sm mb-1.5" style={{ background: `linear-gradient(90deg, ${p.swatch[0]}, ${p.swatch[1]})` }} />
              <p className="text-[11px] font-medium leading-tight">{p.name}</p><p className="text-[10px] text-slate-500 leading-tight">{p.ref}</p>
            </button>
          ))}
        </div>
      </Section>
      <Section title="Colour wheels" right={<button onClick={() => setGrade({ ...DEFAULT_GRADE })} className="text-xs text-slate-400 hover:text-[#FFB800] flex items-center gap-1" data-testid="grade-reset-button"><RotateCcw className="w-3 h-3" />Reset all</button>}>
        <div className="flex justify-between">
          {["lift", "gamma", "gain"].map((w) => <ColourWheel key={w} label={w} value={grade[w]} onChange={set(w)} testId={`${w}-wheel`} />)}
        </div>
      </Section>
      <Section title="Primaries">
        {PRIMARIES.map(([k, l, mn, mx]) => <Param key={k} label={l} value={grade[k]} min={mn} max={mx} onChange={set(k)} testId={`grade-${k}`} />)}
      </Section>
      <Section title="Depth & density">
        <Param label="Tonal depth" value={grade.depth} min={0} max={1} onChange={set("depth")} testId="grade-depth" />
        <Param label="Colour density" value={grade.density} min={0} max={1} onChange={set("density")} testId="grade-density" />
        <Param label="Look strength" value={grade.mix} min={0} max={1} onChange={set("mix")} testId="grade-mix" />
        <p className="text-[11px] text-slate-500 mt-2">Depth adds a filmic S-curve and richer blacks; density darkens saturated colour like a print stock.</p>
      </Section>
      <Section title="Export LUT">
        <div className="flex gap-2">
          <Input value={lutName} onChange={(e) => setLutName(e.target.value)} placeholder="LUT name" className="bg-[#0B0C10] h-9" data-testid="lut-name-input" />
          <Select value={size} onValueChange={setSize}>
            <SelectTrigger className="w-24 h-9 bg-[#0B0C10]" data-testid="lut-size-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="17">17³</SelectItem><SelectItem value="33">33³</SelectItem><SelectItem value="65">65³</SelectItem></SelectContent>
          </Select>
        </div>
        <Button onClick={exportLut} className="w-full mt-2 bg-[#00E5FF] text-[#0B0C10] hover:bg-[#5cefff]" data-testid="export-lut-button"><Download className="w-4 h-4 mr-2" />Download .cube LUT</Button>
        <p className="text-[11px] text-slate-500 mt-2">Works in Final Cut Pro, Premiere Pro, DaVinci Resolve, Avid & more. Apply to Rec.709 footage.</p>
      </Section>
    </div>
  );
};
